import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChefHat, Clock, Trophy, ArrowRight, Flame, Heart } from "lucide-react";
import { api } from "../lib/api";

const MEDAL = ["🥇", "🥈", "🥉"];

const timeLeft = (endsAt) => {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "terminé";
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days}j ${hours}h`;
  return `${hours}h`;
};

/**
 * Bloc Défi cuisine — affiché sur la home sous les catégories.
 * Se cache si aucun challenge actif.
 */
export const ChallengeBlock = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/challenges/current")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.challenge) return null;
  const { challenge, top3 = [], entries_count } = data;

  return (
    <section className="px-4 md:px-12 py-10 max-w-7xl mx-auto" data-testid="home-challenge-block">
      <Link
        to={`/defi/${challenge.slug}`}
        className="group block relative rounded-3xl overflow-hidden border border-brand-red/30"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: challenge.banner_url
              ? `linear-gradient(110deg, rgba(227,27,35,0.72) 0%, rgba(227,27,35,0.3) 50%, rgba(10,6,5,0.5) 100%), url(${challenge.banner_url})`
              : "linear-gradient(110deg, rgba(227,27,35,0.3) 0%, rgba(249,87,56,0.15) 50%, rgba(234,179,8,0.1) 100%)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative grid lg:grid-cols-[1.3fr_1fr] gap-6 p-6 md:p-10 items-center">
          {/* Left text */}
          <div>
            <p className="inline-flex items-center gap-2 tag-pill bg-brand-yellow/15 border-brand-yellow/40 text-brand-yellow mb-3">
              <ChefHat size={12} /> Défi cuisine du mois
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-2 text-balance">
              <span className="mr-3">{challenge.emoji}</span>
              {challenge.dish_name}
            </h2>
            {challenge.theme && (
              <p className="text-lg text-white/85 font-semibold mb-4">{challenge.theme}</p>
            )}

            <div className="flex flex-wrap items-center gap-2 text-xs mb-5">
              <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur rounded-full px-3 py-1.5 text-emerald-400 border border-emerald-500/30">
                <Clock size={11} /> {timeLeft(challenge.ends_at)}
              </span>
              <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur rounded-full px-3 py-1.5 text-white/70 border border-white/10">
                <Flame size={11} /> {entries_count} participant{entries_count > 1 ? "s" : ""}
              </span>
              <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur rounded-full px-3 py-1.5 text-brand-yellow border border-brand-yellow/30">
                <Trophy size={11} /> Top 3 → « Chef du mois »
              </span>
            </div>

            <span className="inline-flex items-center gap-2 btn-primary group-hover:scale-105 transition-transform">
              Participer ou voter <ArrowRight size={14} />
            </span>
          </div>

          {/* Right: top 3 preview or empty state */}
          <div className="rounded-2xl bg-black/40 backdrop-blur-md border border-white/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={14} className="text-brand-yellow" />
              <h3 className="text-[10px] uppercase tracking-widest font-black text-white/80">Top 3 actuel</h3>
            </div>
            {top3.length === 0 ? (
              <div className="text-sm text-white/60 text-center py-5">
                🔥 Pas encore de participation.<br />
                <span className="text-brand-yellow font-semibold">Sois le·la premier·ère !</span>
              </div>
            ) : (
              <ul className="space-y-1.5">
                {top3.map((t, i) => (
                  <li key={t.entry_id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/[0.05]">
                    <span className="text-base shrink-0">{MEDAL[i]}</span>
                    {t.cover_path ? (
                      <img src={t.cover_path} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center shrink-0">
                        <ChefHat size={12} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{t.title}</p>
                      <p className="text-[10px] text-white/50 truncate">{t.artist_name}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-red">
                      <Heart size={10} className="fill-current" /> {t.votes}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Link>
    </section>
  );
};

export default ChallengeBlock;
